import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useI18n } from "../../lib/i18n.jsx";
import { PRODUCTS } from "../../lib/products.js";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuoteForm({ open, onClose, defaultProduct }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    country: "",
    product_id: "",
    quantity: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        product_id: defaultProduct?.id || "",
      }));
    }
  }, [open, defaultProduct]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, { ...form, language: lang });
      toast.success(t.quote.success);
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        country: "",
        product_id: "",
        quantity: "",
        message: "",
      });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t.quote.error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#f7f7f4] rounded-xl px-4 py-3 text-sm outline-none border border-[#e5e8e2] focus:border-[#133322] transition";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 bg-white border-[#e5e8e2] rounded-2xl"
        data-testid="quote-modal"
      >
        <DialogTitle className="sr-only">{t.quote.title}</DialogTitle>
        <DialogDescription className="sr-only">{t.quote.subtitle}</DialogDescription>
        <form onSubmit={submit} className="p-8 lg:p-10">
          <div className="eu-data-label mb-3">
            <i className="fa-solid fa-envelope mr-2"></i>
            EUZERONE · CONTACT
          </div>
          <h3 className="font-display text-3xl lg:text-4xl text-[#133322] leading-tight">
            {t.quote.title}
          </h3>
          <p className="mt-2 text-sm text-[#5c6b61]">{t.quote.subtitle}</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="eu-data-label block mb-2">{t.quote.name}*</label>
              <input
                required
                value={form.name}
                onChange={update("name")}
                className={inputCls}
                data-testid="quote-name"
              />
            </div>
            <div>
              <label className="eu-data-label block mb-2">{t.quote.email}*</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                className={inputCls}
                data-testid="quote-email"
              />
            </div>
            <div>
              <label className="eu-data-label block mb-2">{t.quote.phone}</label>
              <input
                value={form.phone}
                onChange={update("phone")}
                className={inputCls}
                data-testid="quote-phone"
              />
            </div>
            <div>
              <label className="eu-data-label block mb-2">{t.quote.company}</label>
              <input
                value={form.company}
                onChange={update("company")}
                className={inputCls}
                data-testid="quote-company"
              />
            </div>
            <div>
              <label className="eu-data-label block mb-2">{t.quote.country}</label>
              <input
                value={form.country}
                onChange={update("country")}
                className={inputCls}
                data-testid="quote-country"
              />
            </div>
            <div>
              <label className="eu-data-label block mb-2">{t.quote.quantity}</label>
              <input
                value={form.quantity}
                onChange={update("quantity")}
                className={inputCls}
                placeholder="ej. 100 kg"
                data-testid="quote-quantity"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="eu-data-label block mb-2">{t.quote.product}</label>
              <select
                value={form.product_id}
                onChange={update("product_id")}
                className={inputCls}
                data-testid="quote-product"
              >
                <option value="">{t.quote.any_product}</option>
                {PRODUCTS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name[lang]} ({p.npk})
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="eu-data-label block mb-2">{t.quote.message}</label>
              <textarea
                value={form.message}
                onChange={update("message")}
                rows={4}
                className={inputCls}
                data-testid="quote-message"
              />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-[#5c6b61] hover:text-[#133322]"
              data-testid="quote-cancel"
            >
              {t.productDetail.back}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="eu-btn-primary"
              data-testid="quote-submit"
            >
              <i className="fa-solid fa-paper-plane text-xs"></i>
              {submitting ? t.quote.submitting : t.quote.submit}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
